import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  TemplateRef
} from '@angular/core';

import {
  CustomTableCellDirective,
  SuffixCellContentDirective
} from '@app/eds/directives';

import { TextType } from '..';

import { TableColumnDividerTypes } from '@app/eds';
import {
  ICoumnState,
  IGroupHeaderType,
  ISelectedColumn,
  ITableColumnDetailsType,
  ResetStateSortIcon,
  SharedConstants,
  SharedCssClassNames,
  SharedCSSPropertyName,
  SharedElementSizingUnits,
  SortingOrder,
  TableIcons,
  TableStrings
} from '@app/eds';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit, OnChanges {
  @Input() data: any[] = [];

  @ContentChild('header', { static: false }) headerTemplateRef?: TemplateRef<Component>;

  @ContentChild('row', { static: false }) rowTemplateRef?: TemplateRef<Component>;

  @ContentChild('footer', { static: false }) footerTemplateRef?: TemplateRef<Component>;

  @ContentChild('lastColumnHeader', { static: false })
  lastColumnHeaderTemplateRef?: TemplateRef<Component>;

  @ContentChild('lastColumnValue', { static: false })
  lastColumnValueTemplateRef?: TemplateRef<Component>;

  @ContentChild('lastColumnFooter', { static: false })
  lastColumnFooterTemplateRef?: TemplateRef<Component>;

  @ContentChild(CustomTableCellDirective, { read: TemplateRef })
  customRowTemplateRef?: TemplateRef<Component>;

  @ContentChild(SuffixCellContentDirective, { read: TemplateRef })
  suffixCellContentTemplate!: TemplateRef<any>;

  @Input() displayedColumnDetails: ITableColumnDetailsType[] = [];
  @Input() headerClassName: string = SharedCssClassNames.TEXT_LEFT_ALIGN;
  @Input() footerClassName: string = SharedCssClassNames.TEXT_LEFT_ALIGN;
  @Input() rowClassName: string = SharedCssClassNames.TEXT_LEFT_ALIGN;
  @Input() showFooter = false;
  @Input() footers: string[] = [];
  @Input() active = false;
  @Input() customizeLastColumn = false;
  @Input() groupHeader = false;
  @Input() groupHeaderClassName: string = SharedConstants.EMPTY_STRING;
  @Input() tableClassName: string = SharedConstants.EMPTY_STRING;
  @Input() heightTableViewContainer!: { 'max-height': string };

  // stores the length up to which we will show the data and rest will be shown in tooltip
  @Input() showTextUpto: number = TableStrings.TABLE_TEXT_MAX_LENGTH_SHOW;

  // Types of Column dividers.
  columnDividerTypes = TableColumnDividerTypes;

  // table body row style
  @Input() textClassName: TextType = 'body2';

  // table header style
  @Input() tableHeaderClass: TextType = 'table-header';

  // Checks whether column sorting is required or not. Ex. reporting table
  @Input() isColumnSorting = false;

  // Provides info about default active column's name
  @Input() defaultActiveColumnName: string = SharedConstants.EMPTY_STRING;

  // holds current active col name
  @Input() currentActiveColumnName: string = SharedConstants.EMPTY_STRING;

  // holds current active sort order
  @Input() currentSortOrder: string = SharedConstants.EMPTY_STRING;

  // Provides info about currently active column's name
  activeColumnName: string = SharedConstants.EMPTY_STRING;

  // tracks whether rows are fetched or not
  @Input() isRowDataLoading = false;

  // tells sort column and order should be reset to default or not.
  @Input() shouldresetSort = true;

  // Minimum width of the table
  @Input() minWidth!: string;

  // Emits the clicked row data to parent
  @Output() tableRowData = new EventEmitter();

  // Emits the sorting icon clicked column's header data to parent
  @Output() sortIconClicked = new EventEmitter<ISelectedColumn>();

  public displayedColumns: string[] = [];

  // provide an default icons sorting for all columns.
  public defaultSortingIcon: string = TableIcons.ARROW_UP;

  // Tracked  status sort of columns;
  public columnState: Array<ICoumnState> = [];

  // Tracked number click sort of columns
  private countClickedSort = SharedConstants.ZERO;

  ngOnInit() {
    if (
      this.customizeLastColumn &&
      !this.displayedColumnDetails.find(
        (col) =>
          col.display === TableStrings.DISPLAY_NAME &&
          col.property === TableStrings.PROPERTY_NAME
      )
    ) {
      this.displayedColumnDetails.push({
        display: TableStrings.DISPLAY_NAME,
        property: TableStrings.PROPERTY_NAME
      });
    }

    this.setData();
  }

  ngOnChanges(): void {
    this.setData();
  }

  /**
   * Sets the columns and table data
   */
  setData = () => {
    //extract the column names into an array
    this.displayedColumns = this.displayedColumnDetails.map((col) => col.property);

    if (this.isColumnSorting || this.shouldresetSort) {
      this.activeColumnName = this.shouldresetSort
        ? this.defaultActiveColumnName
        : this.activeColumnName;

      this.columnState = this.displayedColumnDetails.map((column) => {
        const sortedIcon =
          column.sortDefault && column.sortDefault === SortingOrder.ASC
            ? TableIcons.ARROW_DOWN
            : TableIcons.ARROW_UP;

        const _state: ICoumnState = {
          property: column.property,
          sorted: sortedIcon
        };

        if (column.sortDefault) {
          _state.default =
            column.sortDefault && column.sortDefault === SortingOrder.ASC
              ? TableIcons.ARROW_DOWN
              : TableIcons.ARROW_UP;
        }

        return _state;
      });
    }

    if (this.currentActiveColumnName) {
      this.activeColumnName = this.currentActiveColumnName;

      this.columnState = this.displayedColumnDetails.map((col) => {
        let defaultSorting = TableIcons.ARROW_UP;
        if (col.sortDefault) {
          defaultSorting =
            col.sortDefault === SortingOrder.ASC
              ? TableIcons.ARROW_UP
              : TableIcons.ARROW_DOWN;
        }

        if (col.property === this.currentActiveColumnName) {
          let currentSortIcon = defaultSorting;
          // current sort order == empty set default arrow is upward
          if (this.currentSortOrder === SharedConstants.EMPTY_STRING) {
            this.activeColumnName = '';
          } else {
            currentSortIcon =
              this.currentSortOrder === SortingOrder.DESC
                ? TableIcons.ARROW_DOWN
                : TableIcons.ARROW_UP;
          }

          return {
            property: col.property,
            sorted: currentSortIcon,
            default: defaultSorting
          };
        }

        return {
          property: col.property,
          sorted: defaultSorting,
          default: defaultSorting
        };
      });
    }
  };

  /**
   * This method return the initial value for tooltip
   *
   * @param {string[]} item
   * @returns {string} single item
   */
  getTooltipContent = (item: string[]) =>
    item && item.length > this.showTextUpto
      ? `${item.slice(0, this.showTextUpto)}...`
      : item;

  /**
   * Function to group table rows
   *
   * @param {index: number, item:{}}
   * @returns boolean
   */
  isGroup = (index: number, item: IGroupHeaderType) => item.isGroupBy;

  /**
   * Function to which return css class for table column
   *
   * @param {columnData:ITableColumnDetailsType}
   * @returns string
   */
  getColumnClass = (columnData: ITableColumnDetailsType) =>
    columnData.columnTextBold ? 'table-column-bold' : this.textClassName;

  /**
   * Function which emits the row data
   *
   * @param {rowData:any}
   */
  getRowData = (rowData: any) => this.tableRowData.emit(rowData);

  /**
   * This method will handle the sorting icon click and
   * this will emit an event with currently select column's property
   * and the sorting order.
   *
   * @param {ITableColumnDetailsType} columnData
   */
  public triggeredSortingIconClick(columnData: ITableColumnDetailsType) {
    if (columnData.property !== this.activeColumnName) {
      this.activeColumnName = columnData.property;
    }

    let activeSort = SharedConstants.EMPTY_STRING;
    this.columnState = this.displayedColumnDetails.map((column, index) => {
      const _statecol = this.columnState[index];

      if (column.property === columnData.property) {
        let newStateIcon = TableIcons.ARROW_UP;
        if (
          !this.currentActiveColumnName ||
          this.currentActiveColumnName !== columnData.property
        ) {
          newStateIcon = _statecol.sorted;
          this.countClickedSort = SharedConstants.ONE;
        } else {
          newStateIcon =
            _statecol.sorted === TableIcons.ARROW_UP
              ? TableIcons.ARROW_DOWN
              : TableIcons.ARROW_UP;
          this.countClickedSort++;
        }

        // Set Active Sort the current Sort Arrow user clicked
        activeSort =
          newStateIcon === TableIcons.ARROW_UP ? SortingOrder.ASC : SortingOrder.DESC;

        // if current sort order not null and current state are sorted with same as default state mean next state is not sorted.
        if (
          this.currentSortOrder &&
          this.currentActiveColumnName === this.activeColumnName &&
          this.countClickedSort === ResetStateSortIcon
        ) {
          this.activeColumnName = '';
          activeSort = '';
          this.countClickedSort = SharedConstants.ZERO;
        }

        return {
          ..._statecol,
          sorted: newStateIcon
        };
      }

      return {
        ..._statecol,
        sorted: _statecol.default || this.defaultSortingIcon
      };
    });

    this.sortIconClicked.emit({
      property: this.activeColumnName,
      order: activeSort
    });
  }

  /**
   * This method will help to get the class name
   * in case of the column sorting.
   *
   * @param {ITableColumnDetailsType} item
   * @returns {string} class name
   */
  getHeaderClassName = (item: ITableColumnDetailsType) =>
    item.property === this.activeColumnName && this.isColumnSorting
      ? 'shared-table-header-sort-icon-divider-active'
      : 'shared-table-header-sort-icon-divider';

  /**
   * If value is undefined, then it returns - , otherwise converts
   * to string and returns it.
   *
   * @param {number | string | undefined} value
   * @returns {string}
   */
  convertValueToString = (value: number | string | undefined) =>
    value || value === 0 ? value.toString() : '-';

  /**
   * Returns the styling of the table, specifically the min-width
   * if passed from parent component.
   *
   * @returns { [key: string]: string } stylingObj
   */
  getTableStyling = () => {
    const stylingObj: { [key: string]: string } = {};

    if (this.minWidth) {
      stylingObj[SharedCSSPropertyName.MIN_WIDTH] = this.minWidth;
    }

    return stylingObj;
  };

  /**
   * Returns the styling of the table column, specifically the col-width
   * if passed from parent component.
   *
   * @param { tableColumnDetailsType } column
   * @returns { [key: string]: string } stylingObj
   */
  getColumnStyling = (column: ITableColumnDetailsType) => {
    const stylingObj: { [key: string]: string } = {};

    if (column.columnWidth) {
      stylingObj[
        SharedCSSPropertyName.WIDTH
      ] = `${column.columnWidth}${SharedElementSizingUnits.PIXEL}`;
    }

    return stylingObj;
  };
}
